import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Returns the list of Cloudinary photos for a given category.
 * - `null` while the fetch is in flight (used by callers to hide <img> placeholders and avoid flashing stock imagery)
 * - `[]`   when the API returns nothing
 * - `[...]` when photos exist
 */
export function useCategoryPhotos(category) {
  const [photos, setPhotos] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setPhotos(null);
    axios
      .get(`${API}/photos`, { params: { category } })
      .then(({ data }) => { if (!cancelled) setPhotos(data || []); })
      .catch(() => { if (!cancelled) setPhotos([]); });
    return () => { cancelled = true; };
  }, [category]);

  return photos;
}

/**
 * Returns the uploaded photo URL at index `i`, or `null` if unavailable.
 * The 3rd argument is accepted for backwards compatibility with existing callers
 * but is intentionally ignored — no stock fallbacks are ever used.
 */
export function pickPhoto(photos, index /* , _fallback */) {
  if (!photos) return null;
  const p = photos[index];
  return p?.url || null;
}

/**
 * Picks a photo whose `title` matches (case-insensitive substring) or falls
 * through to index-based lookup. Never returns a stock fallback — returns `null`
 * when nothing is uploaded yet.
 */
export function pickPhotoByTitle(photos, titleSubstr, indexFallback /* , _fallback */) {
  if (!photos) return null;
  const needle = (titleSubstr || "").toLowerCase();
  const match = photos.find((p) => (p.title || "").toLowerCase().includes(needle));
  return match?.url || pickPhoto(photos, indexFallback);
}
